import * as React from 'react';
import { getService, IService } from '../../API/GoMoto';
import { Types, useCart, useCartDispatch } from '../../context/cart';
import { useKioskLocalization, useKioskState } from '../../context/Kiosk/kiosk';
import { CustomQuestionType } from '../../API/Enums';
import { CTAButton } from '../../controls/ButtonBase';
import { useLocalization } from '../../context/Localization/localization';
import { Heading } from '../../controls/Heading/Heading';
import { ScrollableBody } from '../Layout/ScrollableBody/ScrollableBody';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMAppointmentName } from '../../controls/AppointmentName';

export interface IProps {
    onProceed: () => void;
    onSkip: () => void;
}

const CUSTOM_QUESTION_COMMENT_KEY = 'CustomUpsell';

export const CustomUpsell: React.FC<IProps> = (props) => {
    const Localization = useLocalization();
    const kioskLocalization = useKioskLocalization();
    const kiosk = useKioskState();
    const cart = useCart();
    const cartDispatch = useCartDispatch();
    const [customService, setCustomService] = React.useState<IService>();
    const { customQuestionType, customQuestionCommentOrOpcode } = kiosk.dealer;
    const enabled = customQuestionType !== CustomQuestionType.None;

    React.useEffect(() => {
        if (!enabled) {
            props.onSkip();
        }
    }, [enabled, props]);

    const getTargetService = async (): Promise<IService | undefined> => {
        if (customService) {
            return customService;
        }
        const targetService = await getService(kiosk.key, customQuestionCommentOrOpcode);
        if (targetService) {
            setCustomService(targetService);
        }

        return targetService ?? undefined;
    };

    const onProceed = async (accepted: boolean) => {
        if (customQuestionType === CustomQuestionType.Service) {
            const service = await getTargetService();
            if (service) {
                const inCart = cart.services.some((s) => s.id === service.id);
                if (accepted && !inCart) {
                    cartDispatch({ type: Types.AddService, payload: service });
                }
                else if (!accepted && inCart) {
                    cartDispatch({ type: Types.RemoveService, payload: { id: service.id } });
                }
            }
        }
        else if (customQuestionType === CustomQuestionType.Comment) {
            cartDispatch(
                accepted
                    ? {
                        type: Types.UpdateComment,
                        payload: { key: CUSTOM_QUESTION_COMMENT_KEY, comment: `Customer is interested in ${customQuestionCommentOrOpcode}` },
                    }
                    : {
                        type: Types.RemoveComment,
                        payload: { key: CUSTOM_QUESTION_COMMENT_KEY, comment: '' },
                    },
            );
        }
        props.onProceed();
    };

    return enabled ? (
        <ScrollableBody
            heading={<Heading primaryHeading={<GMAppointmentName />} secondaryHeading={kioskLocalization.dealer.customUpsellPageText} />}
            proceedButtons={
                <GMButtonGroup
                    buttons={[
                        (
                            <CTAButton
                                styleType={'primary'}
                                onClick={() => onProceed(true)}
                                title={Localization.ClickToConfirmSpecialOffer}
                                label={Localization.Yes}
                                id={Localization.ClickToConfirmSpecialOffer}
                                ariaLabel={Localization.ClickToConfirmSpecialOffer}
                            />
                        ),
                        (
                            <CTAButton
                                styleType={'secondary'}
                                onClick={() => onProceed(false)}
                                title={Localization.ClickToSkipSpecialOffer}
                                label={Localization.No}
                                id={Localization.ClickToSkipSpecialOffer}
                                ariaLabel={Localization.ClickToSkipSpecialOffer}
                            />
                        ),
                    ]}
                />
            }
        />
    ) : null;
};
